// src/encounter/care-plan-readout.tsx
//
// CarePlanReadout · Signal level: block · FHIR: CarePlan (goals: Goal).
//
// The encounter's care-plan READOUT, ported 1:1 from the R6c Encounter Frame (design desk export
// docs/design-desk/exports/R6c/src/"Encounter Frame.dc.html", kind 'careplan', the isCarePlan block),
// under design desk D-14 and D-43. FREEZE-MANIFEST has no CarePlan anchor; per D-43 the R6c export is
// the anchor. DS graduation of this block comes after the 1 Oct pitch (D-43).
//
// Rules held here:
//   · A readout. No verbs, for any persona (D-14). The only control is the expand/collapse row.
//   · Collapsed to one line by default; local state only (D-43 §1).
//   · Nothing while loading or with no plan; ONE line in words on a failed read (D-14, D-43 §6).
//   · The goal verdict is the SERVER's, carried, never recomputed (care-plan-view.ts header). The tally
//     counts the server's 'met' verdicts; it does not compare values to targets.
//   · No wayfinding colour on the block; it inherits the Plan section's (D-43 §7).
//   · The plan line names the patient's ENROLMENT, never this visit's purpose (see `planLine`): the
//     readout shows for every visit of an enrolled patient, whatever the visit was booked for.
//
// The parent decides where it sits (the encounter frame is the chair's, A7). This file does not mount
// itself anywhere.
import { useId, useState } from 'react';
import { cn, StatusChip, TEXT_ROLE_CLASSES, TEXT_TONE_CLASSES } from '@scripsteam/scrips-signal-ds';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { CarePlanView, GoalStatus, GoalView } from './lib/care-plan-view';
import { useCarePlanReadout, type CarePlanReadoutState } from './services/use-care-plans';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "18 Jul 2026" (D Mon YYYY) from an ISO string, by its UTC calendar date; the raw text when unreadable. */
export function formatReadoutDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

const VERDICT: Record<GoalStatus, { semantic: 'success' | 'warning' | 'neutral'; label: string }> = {
  met: { semantic: 'success', label: 'Met' },
  'not-met': { semantic: 'warning', label: 'Not met' },
  'no-data': { semantic: 'neutral', label: 'No result yet' },
  unknown: { semantic: 'neutral', label: 'Status not recognised' },
};

/** "Target <5.7 % · ADA 2026 · due 24 Dec 2026" (D-43 §4). */
export function targetLine(goal: GoalView): string {
  if (!goal.target) return 'Target not readable';
  const { comparator, value, unit, source } = goal.target;
  const parts = [`Target ${comparator}${value}${unit ? ` ${unit}` : ''}`, source ?? 'source not recorded'];
  if (goal.dueDate) parts.push(`due ${formatReadoutDate(goal.dueDate)}`);
  return parts.join(' · ');
}

/** "Latest 5.3 % as of 18 Jul 2026": the lab's text when it sent one, else the value (D-43 §4). */
export function latestLine(goal: GoalView): string {
  const l = goal.latest;
  if (!l) return 'No result on file';
  const shown = l.valueText ?? (l.value === null ? '' : String(l.value));
  return `Latest ${shown}${l.unit ? ` ${l.unit}` : ''} as of ${formatReadoutDate(l.date)}`;
}

/**
 * The collapsed row: the PATIENT'S enrolment, never this visit's purpose.
 *
 * It used to read "{programme} · {step}", drawn in the A&P of a visit booked for an unrelated reason
 * (dev test walk, 1 Oct): the step read as this visit's reason and contradicted the header's
 * "Reason for Visit". Nothing on the wire says this appointment IS the step's visit (C4's
 * `currentStep` is `{ id, name }` only, C5 carries no appointment or encounter), so the line cannot
 * claim it and does not: an active plan says the patient is enrolled, and its step is named as the
 * CARE PLAN's step (Samer, 18 Oct: "care plan", not "programme"). A plan that is not active keeps
 * its title alone, as before (it never carries a step: `withCurrentSteps` only joins one to an active plan).
 */
export function planLine(plan: CarePlanView): string {
  if (plan.status !== 'active') return plan.title;
  const enrolled = `Enrolled in ${plan.title}`;
  return plan.currentStep.state === 'known' ? `${enrolled} · care plan step: ${plan.currentStep.name}` : enrolled;
}

export function tally(plan: CarePlanView): string {
  const met = plan.goals.filter((g) => g.status === 'met').length;
  return `Goals on target ${met}/${plan.goals.length}`;
}

function PlanBlock({ plan }: { plan: CarePlanView }) {
  const [open, setOpen] = useState(false);
  const goalsId = useId();
  return (
    <div data-block="CarePlan" className="flex flex-col">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        // Only while the list exists: a control must not point at an unmounted id.
        aria-controls={open ? goalsId : undefined}
        className="flex min-h-10 w-full cursor-pointer items-center gap-2 rounded-xl border border-border-default bg-surface-card px-4 py-3 text-left hover:bg-surface-background"
      >
        <span className={cn('inline-flex', TEXT_TONE_CLASSES.secondary)}>
          {open ? <ChevronDown className="size-4" aria-hidden /> : <ChevronRight className="size-4" aria-hidden />}
        </span>
        <span className={cn('min-w-0 flex-1', TEXT_ROLE_CLASSES.body)}>{planLine(plan)}</span>
        <span className={cn('whitespace-nowrap tabular-nums', TEXT_ROLE_CLASSES['meta-strong'], TEXT_TONE_CLASSES.secondary)}>
          {tally(plan)}
        </span>
      </button>
      {open && (
        <ul id={goalsId} aria-label={`Goals of ${plan.title}`} className="flex flex-col">
          {plan.goals.map((goal) => {
            const verdict = VERDICT[goal.status];
            return (
              <li key={goal.id} data-block="Goal" className="flex flex-col gap-1 border-b border-border-subtle px-4 py-3">
                <div className="flex items-start justify-between gap-4">
                  <span className={cn('min-w-0 flex-1', TEXT_ROLE_CLASSES['body-emphasis'])}>{goal.title}</span>
                  <span className="shrink-0">
                    <StatusChip semantic={verdict.semantic} tone="soft">
                      {verdict.label}
                    </StatusChip>
                  </span>
                </div>
                <span className={cn(TEXT_ROLE_CLASSES.meta, TEXT_TONE_CLASSES.secondary)}>{targetLine(goal)}</span>
                <span className={cn(TEXT_ROLE_CLASSES.body, TEXT_TONE_CLASSES.primary)}>{latestLine(goal)}</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/** Presentational: renders exactly what `state` says (D-14). */
export function CarePlanReadoutView({ state, className }: { state: CarePlanReadoutState; className?: string }) {
  if (state.kind === 'hidden') return null;
  if (state.kind === 'error') {
    return <p className={cn(TEXT_ROLE_CLASSES.body, TEXT_TONE_CLASSES.secondary, className)}>Care plan couldn&apos;t be loaded.</p>;
  }
  return (
    <section aria-label="Care plan" className={cn('flex flex-col gap-2', className)}>
      {state.plans.map((plan) => (
        <PlanBlock key={plan.carePlanId} plan={plan} />
      ))}
    </section>
  );
}

/** The readout for one patient: C5 plans + C4 current steps. */
export function CarePlanReadout({ patientId, className }: { patientId: string | null | undefined; className?: string }) {
  return <CarePlanReadoutView state={useCarePlanReadout(patientId)} className={className} />;
}
